import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BarChart3, TrendingUp, ShoppingCart, Users, Euro, Calendar, ArrowUp, ArrowDown, ChevronsUpDown
} from 'lucide-react';
import {
  LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import api from '../../services/api';
import { handleApiError } from '../../utils/errorHandling';
import { useAuth } from '../../contexts/AuthContext';
import ActivityLogPanel from '../../components/Achat/ActivityLogPanel';

interface DashboardStats {
  total_orders: number;
  total_revenue: number;
  active_buyers: number;
  total_consumables: number;
  orders_by_status: Record<string, number>;
  revenue_by_month: Array<{ month: string; revenue: number }>;
  orders_by_month: Array<{ month: string; orders: number }>;
  top_buyers: Array<{ buyer_name: string; total_spent: number; order_count: number }>;
  top_products: Array<{ product_name: string; quantity_sold: number; revenue: number }>;
  recent_orders: Array<any>;
  client_stats: {
    prospects: number;
    actifs: number;
    inactifs: number;
    bloques: number;
    fermes: number;
  };
  apporteur_client_stats: Array<{
    admin_id: number;
    first_name?: string | null;
    last_name?: string | null;
    company_name?: string | null;
    email?: string | null;
    prospects: number;
    prospects_r1: number;
    prospects_r2_plus: number;
    actifs: number;
    inactifs: number;
    bloques: number;
    fermes: number;
    total: number;
  }>;
}

const STATUS_LABELS: Record<string, string> = {
  draft: 'Brouillon',
  pending: 'En attente',
  confirmed: 'Confirmée',
  shipped: 'Expédiée',
  delivered: 'Livrée',
  invoiced: 'Facturée',
  paid: 'Payée',
  cancelled: 'Annulée',
  enregistrer: 'Enregistrée',
};

const STATUS_COLORS: Record<string, string> = {
  draft: '#94a3b8',      // slate-400
  pending: '#eab308',    // yellow-500
  confirmed: '#3b82f6',  // blue-500
  shipped: '#a855f7',    // purple-500
  delivered: '#22c55e',  // green-500
  invoiced: '#f59e0b',   // amber-500
  paid: '#10b981',       // emerald-500
  enregistrer: '#6366f1',  // indigo-500
  cancelled: '#6b7280',  // gray-500
};

type DashboardSortColumn = 'name' | 'prospects' | 'prospects_r1' | 'prospects_r2_plus' | 'actifs' | 'inactifs' | 'bloques' | 'fermes' | 'total';

function DashboardSortIcon({ column, sortColumn, sortDirection }: { column: DashboardSortColumn; sortColumn: DashboardSortColumn | null; sortDirection: 'asc' | 'desc' }) {
  if (sortColumn !== column) {
    return <ChevronsUpDown className="h-3 w-3 ml-1 opacity-40" />;
  }
  return sortDirection === 'asc'
    ? <ArrowUp className="h-3 w-3 ml-1 text-blue-600" />
    : <ArrowDown className="h-3 w-3 ml-1 text-blue-600" />;
}

const AchatDashboardTailwind: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [sortColumn, setSortColumn] = useState<DashboardSortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const isGlobalAdmin = user?.role === 'super_admin' || user?.role === 'admin';

  useEffect(() => {
    void fetchDashboardData();
  }, [period]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Single API call to get all dashboard stats
      const statsRes = await api.get('/dashboard/achat/stats');
      const stats = statsRes.data;

      setStats(stats);
    } catch (error: any) {
      handleApiError(error, 'Erreur chargement dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (column: DashboardSortColumn) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedApporteurStats = useMemo(() => {
    if (!stats?.apporteur_client_stats || !sortColumn) {
      return stats?.apporteur_client_stats ?? [];
    }

    return [...stats.apporteur_client_stats].sort((a, b) => {
      let aValue: string | number;
      let bValue: string | number;

      if (sortColumn === 'name') {
        aValue = `${a.first_name ?? ''} ${a.last_name ?? ''}`.trim() || a.company_name || a.email || '';
        bValue = `${b.first_name ?? ''} ${b.last_name ?? ''}`.trim() || b.company_name || b.email || '';
        const comparison = aValue.localeCompare(bValue, 'fr', { sensitivity: 'base' });
        return sortDirection === 'asc' ? comparison : -comparison;
      } else {
        aValue = a[sortColumn];
        bValue = b[sortColumn];
        return sortDirection === 'asc' ? (aValue as number) - (bValue as number) : (bValue as number) - (aValue as number);
      }
    });
  }, [stats?.apporteur_client_stats, sortColumn, sortDirection]);

  const apporteurTotals = useMemo(() => {
    if (!stats?.apporteur_client_stats || stats.apporteur_client_stats.length === 0) {
      return {
        prospects: 0,
        prospects_r1: 0,
        prospects_r2_plus: 0,
        actifs: 0,
        inactifs: 0,
        bloques: 0,
        fermes: 0,
        total: 0
      };
    }

    return stats.apporteur_client_stats.reduce((acc, apporteur) => ({
      prospects: acc.prospects + apporteur.prospects,
      prospects_r1: acc.prospects_r1 + apporteur.prospects_r1,
      prospects_r2_plus: acc.prospects_r2_plus + apporteur.prospects_r2_plus,
      actifs: acc.actifs + apporteur.actifs,
      inactifs: acc.inactifs + apporteur.inactifs,
      bloques: acc.bloques + apporteur.bloques,
      fermes: acc.fermes + apporteur.fermes,
      total: acc.total + apporteur.total
    }), {
      prospects: 0,
      prospects_r1: 0,
      prospects_r2_plus: 0,
      actifs: 0,
      inactifs: 0,
      bloques: 0,
      fermes: 0,
      total: 0
    });
  }, [stats?.apporteur_client_stats]);

  if (loading || !stats) {
    return (
      <div className="h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 via-blue-50/20 to-slate-50 dark:from-slate-950 dark:via-blue-950/10 dark:to-slate-950">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const statusData = Object.entries(stats.orders_by_status).map(([statusKey, value]) => ({
    name: STATUS_LABELS[statusKey] ?? statusKey,
    value,
    key: statusKey,
  }));

  // Calculate month-over-month comparisons
  const calculateComparison = (data: Array<{ month: string; revenue?: number; orders?: number }>, key: 'revenue' | 'orders') => {
    if (data.length < 2) return { percentage: 0, isPositive: true, isNew: false, noChange: true };

    const currentMonth = data[data.length - 1];
    const previousMonth = data[data.length - 2];

    const currentValue = key === 'revenue' ? (currentMonth.revenue || 0) : (currentMonth.orders || 0);
    const previousValue = key === 'revenue' ? (previousMonth.revenue || 0) : (previousMonth.orders || 0);

    if (previousValue === 0 && currentValue === 0) {
      return { percentage: 0, isPositive: true, isNew: false, noChange: true };
    }
    if (previousValue === 0) {
      return { percentage: 0, isPositive: true, isNew: true, noChange: false };
    }

    const percentage = ((currentValue - previousValue) / previousValue) * 100;
    return { percentage: Math.abs(percentage), isPositive: percentage >= 0, isNew: false, noChange: false };
  };

  const revenueComparison = calculateComparison(stats.revenue_by_month, 'revenue');
  const ordersComparison = calculateComparison(stats.orders_by_month, 'orders');

  const statsCardsGridClassName = 'grid grid-cols-4 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-7 gap-2 sm:gap-3';

  const handleApporteurStatClick = (adminId: number, status: 'prospect' | 'actif' | 'inactif' | 'bloque' | 'ferme', rdvStatus?: 'R1' | 'R2+') => {
    const params = new URLSearchParams();
    params.set('admin_id', adminId.toString());
    params.set('status', status);
    if (rdvStatus) params.set('rdv_status', rdvStatus);
    navigate(`/achat/clients?${params.toString()}`);
  };

  return (
    <div className="space-y-6 px-4 sm:px-4 lg:pr-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-3 sm:gap-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-cyan-500 via-blue-500 to-cyan-600 rounded-xl flex items-center justify-center shadow-lg flex-shrink-0">
            <BarChart3 className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-slate-100">Tableau de Bord</h1>
            <p className="text-xs sm:text-sm text-slate-500 dark:text-slate-400">Vue d'ensemble des performances</p>
          </div>
        </div>
        <select
          value={period}
          onChange={(e) => setPeriod(e.target.value)}
          aria-label="Période"
          className="self-end sm:self-auto px-4 py-2 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="week">Cette semaine</option>
          <option value="month">Ce mois</option>
          <option value="quarter">Ce trimestre</option>
          <option value="year">Cette année</option>
        </select>
      </div>

      <div className="space-y-6">
        <div className={statsCardsGridClassName}>
          {/* Chiffre d'affaires */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-2 sm:p-5 border-2 border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 flex flex-col overflow-hidden">
            <div className="flex flex-col items-center sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
              <div className="w-7 h-7 sm:w-10 sm:h-10 bg-gradient-to-br from-blue-100 to-indigo-100 dark:from-blue-900/30 dark:to-indigo-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Euro className="h-3.5 w-3.5 sm:h-5 sm:w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <p className="text-[9px] sm:text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight text-center sm:text-left">CA</p>
            </div>
            <p className="text-sm sm:text-xl lg:text-2xl font-bold text-slate-900 dark:text-slate-100 mt-0.5 sm:mt-1 text-center sm:text-left break-words">
              {Math.round(stats.total_revenue).toLocaleString('fr-FR')} €
            </p>
            <div className="mt-auto pt-1 hidden sm:block">
              {revenueComparison.isNew ? (
                <span className="text-[10px] sm:text-xs font-medium text-blue-600 dark:text-blue-400">Nouveau</span>
              ) : revenueComparison.noChange ? (
                <span className="text-[10px] sm:text-xs text-slate-400 dark:text-slate-500">—</span>
              ) : (
                <div className={`flex items-center gap-1 text-[10px] sm:text-xs ${revenueComparison.isPositive
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
                  }`}>
                  {revenueComparison.isPositive ? (
                    <ArrowUp className="h-3 w-3" />
                  ) : (
                    <ArrowDown className="h-3 w-3" />
                  )}
                  <span>{revenueComparison.isPositive ? '+' : '-'}{revenueComparison.percentage.toFixed(1)}%</span>
                </div>
              )}
            </div>
          </div>

          {/* Commandes */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-2 sm:p-5 border-2 border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 flex flex-col overflow-hidden" style={{ animationDelay: '50ms' }}>
            <div className="flex flex-col items-center sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
              <div className="w-7 h-7 sm:w-10 sm:h-10 bg-gradient-to-br from-orange-100 to-amber-100 dark:from-orange-900/30 dark:to-amber-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <ShoppingCart className="h-3.5 w-3.5 sm:h-5 sm:w-5 text-orange-600 dark:text-orange-400" />
              </div>
              <p className="text-[9px] sm:text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight text-center sm:text-left">Cmdes</p>
            </div>
            <p className="text-sm sm:text-xl lg:text-2xl font-bold text-slate-900 dark:text-slate-100 mt-0.5 sm:mt-1 text-center sm:text-left">{stats.total_orders}</p>
            <div className="mt-auto pt-1 hidden sm:block">
              {ordersComparison.isNew ? (
                <span className="text-[10px] sm:text-xs font-medium text-blue-600 dark:text-blue-400">Nouveau</span>
              ) : ordersComparison.noChange ? (
                <span className="text-[10px] sm:text-xs text-slate-400 dark:text-slate-500">—</span>
              ) : (
                <div className={`flex items-center gap-1 text-[10px] sm:text-xs ${ordersComparison.isPositive
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
                  }`}>
                  {ordersComparison.isPositive ? (
                    <ArrowUp className="h-3 w-3" />
                  ) : (
                    <ArrowDown className="h-3 w-3" />
                  )}
                  <span>{ordersComparison.isPositive ? '+' : '-'}{ordersComparison.percentage.toFixed(1)}%</span>
                </div>
              )}
            </div>
          </div>

          {/* Prospects */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-2 sm:p-5 border-2 border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 flex flex-col overflow-hidden" style={{ animationDelay: '50ms' }}>
            <div className="flex flex-col items-center sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
              <div className="w-7 h-7 sm:w-10 sm:h-10 bg-gradient-to-br from-purple-100 to-violet-100 dark:from-purple-900/30 dark:to-violet-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="h-3.5 w-3.5 sm:h-5 sm:w-5 text-purple-600 dark:text-purple-400" />
              </div>
              <p className="text-[9px] sm:text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight text-center sm:text-left">Prospects</p>
            </div>
            <p className="text-sm sm:text-xl lg:text-2xl font-bold text-slate-900 dark:text-slate-100 mt-0.5 sm:mt-1 text-center sm:text-left">{stats.client_stats.prospects}</p>
            <p className="text-[9px] sm:text-xs text-slate-400 dark:text-slate-500 mt-auto pt-1 hidden sm:block">Sans commande</p>
          </div>

          {/* Actifs */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-2 sm:p-5 border-2 border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 flex flex-col overflow-hidden" style={{ animationDelay: '100ms' }}>
            <div className="flex flex-col items-center sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
              <div className="w-7 h-7 sm:w-10 sm:h-10 bg-gradient-to-br from-green-100 to-emerald-100 dark:from-green-900/30 dark:to-emerald-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="h-3.5 w-3.5 sm:h-5 sm:w-5 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-[9px] sm:text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight text-center sm:text-left">Actifs</p>
            </div>
            <p className="text-sm sm:text-xl lg:text-2xl font-bold text-slate-900 dark:text-slate-100 mt-0.5 sm:mt-1 text-center sm:text-left">{stats.client_stats.actifs}</p>
            <p className="text-[9px] sm:text-xs text-slate-400 dark:text-slate-500 mt-auto pt-1 hidden sm:block">Réguliers</p>
          </div>

          {/* Inactifs */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-2 sm:p-5 border-2 border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 flex flex-col overflow-hidden" style={{ animationDelay: '150ms' }}>
            <div className="flex flex-col items-center sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
              <div className="w-7 h-7 sm:w-10 sm:h-10 bg-gradient-to-br from-yellow-100 to-amber-100 dark:from-yellow-900/30 dark:to-amber-900/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="h-3.5 w-3.5 sm:h-5 sm:w-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <p className="text-[9px] sm:text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight text-center sm:text-left">Inactifs</p>
            </div>
            <p className="text-sm sm:text-xl lg:text-2xl font-bold text-slate-900 dark:text-slate-100 mt-0.5 sm:mt-1 text-center sm:text-left">{stats.client_stats.inactifs}</p>
            <p className="text-[9px] sm:text-xs text-slate-400 dark:text-slate-500 mt-auto pt-1 hidden sm:block">+6 mois</p>
          </div>

          {/* Bloqués */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-2 sm:p-5 border-2 border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 flex flex-col overflow-hidden" style={{ animationDelay: '200ms' }}>
            <div className="flex flex-col items-center sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
              <div className="w-7 h-7 sm:w-10 sm:h-10 bg-gradient-to-br from-red-100 to-red-200 dark:from-red-900/30 dark:to-red-800/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="h-3.5 w-3.5 sm:h-5 sm:w-5 text-red-600 dark:text-red-400" />
              </div>
              <p className="text-[9px] sm:text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight text-center sm:text-left">Bloqués</p>
            </div>
            <p className="text-sm sm:text-xl lg:text-2xl font-bold text-slate-900 dark:text-slate-100 mt-0.5 sm:mt-1 text-center sm:text-left">{stats.client_stats.bloques}</p>
            <p className="text-[9px] sm:text-xs text-slate-400 dark:text-slate-500 mt-auto pt-1 hidden sm:block">Litiges</p>
          </div>

          {/* Fermés */}
          <div className="bg-white dark:bg-slate-900 rounded-xl p-2 sm:p-5 border-2 border-slate-200 dark:border-slate-700 shadow-sm hover:shadow-lg transition-all duration-300 animate-in fade-in slide-in-from-bottom-4 flex flex-col overflow-hidden" style={{ animationDelay: '250ms' }}>
            <div className="flex flex-col items-center sm:flex-row sm:items-center gap-1 sm:gap-2 mb-1">
              <div className="w-7 h-7 sm:w-10 sm:h-10 bg-gradient-to-br from-zinc-100 to-zinc-200 dark:from-zinc-900/30 dark:to-zinc-800/30 rounded-lg flex items-center justify-center flex-shrink-0">
                <Users className="h-3.5 w-3.5 sm:h-5 sm:w-5 text-zinc-700 dark:text-zinc-300" />
              </div>
              <p className="text-[9px] sm:text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wide leading-tight text-center sm:text-left">Fermés</p>
            </div>
            <p className="text-sm sm:text-xl lg:text-2xl font-bold text-slate-900 dark:text-slate-100 mt-0.5 sm:mt-1 text-center sm:text-left">{stats.client_stats.fermes}</p>
            <p className="text-[9px] sm:text-xs text-slate-400 dark:text-slate-500 mt-auto pt-1 hidden sm:block">Clos</p>
          </div>
        </div>

        {isGlobalAdmin && stats.apporteur_client_stats.length > 0 && (
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-4 sm:gap-6">
            <div className="lg:col-span-3 bg-white dark:bg-slate-900 rounded-xl p-3 sm:p-4 border-2 border-slate-200 dark:border-slate-700 shadow-sm">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 mb-3">
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-slate-900 dark:text-slate-100">Apporteurs d'Affaires</h3>
                  <p className="text-[10px] sm:text-xs text-slate-500 dark:text-slate-400">Répartition des statuts par apporteur</p>
                </div>
                <span className="text-[9px] sm:text-[10px] text-slate-400 dark:text-slate-500">Cliquez pour filtrer</span>
              </div>

              {/* Mobile: Card layout */}
              <div className="sm:hidden space-y-3">
                {sortedApporteurStats.map((apporteur) => {
                  const label = `${apporteur.first_name ?? ''} ${apporteur.last_name ?? ''}`.trim() || apporteur.company_name
                    || apporteur.email
                    || `Apporteur #${apporteur.admin_id}`;
                  return (
                    <div key={apporteur.admin_id} className="border border-slate-200 dark:border-slate-700 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2.5">
                        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">{label}</span>
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold text-blue-700 bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300">
                          Total: {apporteur.total}
                        </span>
                      </div>
                      <div className="grid grid-cols-4 gap-2">
                        <button onClick={() => handleApporteurStatClick(apporteur.admin_id, 'prospect')} className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-purple-50 dark:bg-purple-950/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors">
                          <span className="text-lg font-bold text-purple-700 dark:text-purple-300">{apporteur.prospects}</span>
                          <span className="text-[10px] text-purple-600 dark:text-purple-400">Prospects</span>
                        </button>
                        <button onClick={() => handleApporteurStatClick(apporteur.admin_id, 'prospect', 'R1')} className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-indigo-50 dark:bg-indigo-950/20 hover:bg-indigo-100 dark:hover:bg-indigo-900/30 transition-colors">
                          <span className="text-lg font-bold text-indigo-700 dark:text-indigo-300">{apporteur.prospects_r1}</span>
                          <span className="text-[10px] text-indigo-600 dark:text-indigo-400">R1</span>
                        </button>
                        <button onClick={() => handleApporteurStatClick(apporteur.admin_id, 'prospect', 'R2+')} className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-violet-50 dark:bg-violet-950/20 hover:bg-violet-100 dark:hover:bg-violet-900/30 transition-colors">
                          <span className="text-lg font-bold text-violet-700 dark:text-violet-300">{apporteur.prospects_r2_plus}</span>
                          <span className="text-[10px] text-violet-600 dark:text-violet-400">R2+</span>
                        </button>
                        <button onClick={() => handleApporteurStatClick(apporteur.admin_id, 'actif')} className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/20 hover:bg-emerald-100 dark:hover:bg-emerald-900/30 transition-colors">
                          <span className="text-lg font-bold text-emerald-700 dark:text-emerald-300">{apporteur.actifs}</span>
                          <span className="text-[10px] text-emerald-600 dark:text-emerald-400">Actifs</span>
                        </button>
                        <button onClick={() => handleApporteurStatClick(apporteur.admin_id, 'inactif')} className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-yellow-50 dark:bg-yellow-950/20 hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors">
                          <span className="text-lg font-bold text-yellow-700 dark:text-yellow-300">{apporteur.inactifs}</span>
                          <span className="text-[10px] text-yellow-600 dark:text-yellow-400">Inactifs</span>
                        </button>
                        <button onClick={() => handleApporteurStatClick(apporteur.admin_id, 'bloque')} className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-red-50 dark:bg-red-950/20 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors">
                          <span className="text-lg font-bold text-red-700 dark:text-red-300">{apporteur.bloques}</span>
                          <span className="text-[10px] text-red-600 dark:text-red-400">Bloqués</span>
                        </button>
                        <button onClick={() => handleApporteurStatClick(apporteur.admin_id, 'ferme')} className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-zinc-50 dark:bg-zinc-900/20 hover:bg-zinc-100 dark:hover:bg-zinc-900/40 transition-colors">
                          <span className="text-lg font-bold text-zinc-700 dark:text-zinc-300">{apporteur.fermes ?? 0}</span>
                          <span className="text-[10px] text-zinc-600 dark:text-zinc-400">Fermés</span>
                        </button>
                      </div>
                    </div>
                  );
                })}

                {/* Carte Total Général */}
                <div className="border-2 border-slate-300 dark:border-slate-600 rounded-lg p-3 bg-slate-50 dark:bg-slate-800/70">
                  <div className="flex items-center justify-between mb-2.5">
                    <span className="text-sm font-bold text-slate-900 dark:text-slate-100">Total Général</span>
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold text-blue-800 bg-blue-200 dark:bg-blue-900/50 dark:text-blue-200">
                      Total: {apporteurTotals.total}
                    </span>
                  </div>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-purple-200 dark:bg-purple-900/50">
                      <span className="text-lg font-bold text-purple-700 dark:text-purple-200">{apporteurTotals.prospects}</span>
                      <span className="text-[10px] text-purple-600 dark:text-purple-300">Prospects</span>
                    </div>
                    <div className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-indigo-200 dark:bg-indigo-900/50">
                      <span className="text-lg font-bold text-indigo-700 dark:text-indigo-200">{apporteurTotals.prospects_r1}</span>
                      <span className="text-[10px] text-indigo-600 dark:text-indigo-300">R1</span>
                    </div>
                    <div className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-violet-200 dark:bg-violet-900/50">
                      <span className="text-lg font-bold text-violet-700 dark:text-violet-200">{apporteurTotals.prospects_r2_plus}</span>
                      <span className="text-[10px] text-violet-600 dark:text-violet-300">R2+</span>
                    </div>
                    <div className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-emerald-200 dark:bg-emerald-900/50">
                      <span className="text-lg font-bold text-emerald-700 dark:text-emerald-200">{apporteurTotals.actifs}</span>
                      <span className="text-[10px] text-emerald-600 dark:text-emerald-300">Actifs</span>
                    </div>
                    <div className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-yellow-200 dark:bg-yellow-900/50">
                      <span className="text-lg font-bold text-yellow-700 dark:text-yellow-200">{apporteurTotals.inactifs}</span>
                      <span className="text-[10px] text-yellow-600 dark:text-yellow-300">Inactifs</span>
                    </div>
                    <div className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-red-200 dark:bg-red-900/50">
                      <span className="text-lg font-bold text-red-700 dark:text-red-200">{apporteurTotals.bloques}</span>
                      <span className="text-[10px] text-red-600 dark:text-red-300">Bloqués</span>
                    </div>
                    <div className="flex flex-col items-center gap-0.5 p-2 rounded-lg bg-zinc-200 dark:bg-zinc-800/70">
                      <span className="text-lg font-bold text-zinc-700 dark:text-zinc-200">{apporteurTotals.fermes}</span>
                      <span className="text-[10px] text-zinc-600 dark:text-zinc-300">Fermés</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop: Table layout */}
              <div className="hidden sm:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200 dark:border-slate-700">
                      <th
                        className="text-left py-2 text-xs font-medium text-slate-600 dark:text-slate-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('name')}
                      >
                        <span className="inline-flex items-center">
                          Apporteur
                          <DashboardSortIcon column="name" sortColumn={sortColumn} sortDirection={sortDirection} />
                        </span>
                      </th>
                      <th
                        className="text-center py-2 text-xs font-medium text-slate-600 dark:text-slate-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('prospects')}
                      >
                        <span className="inline-flex items-center justify-center">
                          Prosp.
                          <DashboardSortIcon column="prospects" sortColumn={sortColumn} sortDirection={sortDirection} />
                        </span>
                      </th>
                      <th
                        className="text-center py-2 text-xs font-medium text-slate-600 dark:text-slate-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('prospects_r1')}
                      >
                        <span className="inline-flex items-center justify-center">
                          R1
                          <DashboardSortIcon column="prospects_r1" sortColumn={sortColumn} sortDirection={sortDirection} />
                        </span>
                      </th>
                      <th
                        className="text-center py-2 text-xs font-medium text-slate-600 dark:text-slate-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('prospects_r2_plus')}
                      >
                        <span className="inline-flex items-center justify-center">
                          R2+
                          <DashboardSortIcon column="prospects_r2_plus" sortColumn={sortColumn} sortDirection={sortDirection} />
                        </span>
                      </th>
                      <th
                        className="text-center py-2 text-xs font-medium text-slate-600 dark:text-slate-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('actifs')}
                      >
                        <span className="inline-flex items-center justify-center">
                          Actifs
                          <DashboardSortIcon column="actifs" sortColumn={sortColumn} sortDirection={sortDirection} />
                        </span>
                      </th>
                      <th
                        className="text-center py-2 text-xs font-medium text-slate-600 dark:text-slate-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('inactifs')}
                      >
                        <span className="inline-flex items-center justify-center">
                          Inact.
                          <DashboardSortIcon column="inactifs" sortColumn={sortColumn} sortDirection={sortDirection} />
                        </span>
                      </th>
                      <th
                        className="text-center py-2 text-xs font-medium text-slate-600 dark:text-slate-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('bloques')}
                      >
                        <span className="inline-flex items-center justify-center">
                          Bloq.
                          <DashboardSortIcon column="bloques" sortColumn={sortColumn} sortDirection={sortDirection} />
                        </span>
                      </th>
                      <th
                        className="text-center py-2 text-xs font-medium text-slate-600 dark:text-slate-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('fermes')}
                      >
                        <span className="inline-flex items-center justify-center">
                          Ferm.
                          <DashboardSortIcon column="fermes" sortColumn={sortColumn} sortDirection={sortDirection} />
                        </span>
                      </th>
                      <th
                        className="text-center py-2 text-xs font-medium text-slate-600 dark:text-slate-400 cursor-pointer hover:text-blue-600 dark:hover:text-blue-400 transition-colors select-none"
                        onClick={() => handleSort('total')}
                      >
                        <span className="inline-flex items-center justify-center">
                          Tot.
                          <DashboardSortIcon column="total" sortColumn={sortColumn} sortDirection={sortDirection} />
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedApporteurStats.map((apporteur) => {
                      const label = `${apporteur.first_name ?? ''} ${apporteur.last_name ?? ''}`.trim() || apporteur.company_name
                        || apporteur.email
                        || `Apporteur #${apporteur.admin_id}`;
                      return (
                        <tr key={apporteur.admin_id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                          <td className="py-2 text-xs text-slate-900 dark:text-slate-100 font-medium truncate max-w-[120px]" title={label}>{label}</td>
                          <td className="py-2 text-center">
                            <button
                              onClick={() => handleApporteurStatClick(apporteur.admin_id, 'prospect')}
                              className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-purple-700 bg-purple-100 dark:bg-purple-950/30 dark:text-purple-300 hover:bg-purple-200 dark:hover:bg-purple-900/40 transition-colors"
                            >
                              {apporteur.prospects}
                            </button>
                          </td>
                          <td className="py-2 text-center">
                            <button
                              onClick={() => handleApporteurStatClick(apporteur.admin_id, 'prospect', 'R1')}
                              className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-indigo-700 bg-indigo-100 dark:bg-indigo-950/30 dark:text-indigo-300 hover:bg-indigo-200 dark:hover:bg-indigo-900/40 transition-colors"
                            >
                              {apporteur.prospects_r1}
                            </button>
                          </td>
                          <td className="py-2 text-center">
                            <button
                              onClick={() => handleApporteurStatClick(apporteur.admin_id, 'prospect', 'R2+')}
                              className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-violet-700 bg-violet-100 dark:bg-violet-950/30 dark:text-violet-300 hover:bg-violet-200 dark:hover:bg-violet-900/40 transition-colors"
                            >
                              {apporteur.prospects_r2_plus}
                            </button>
                          </td>
                          <td className="py-2 text-center">
                            <button
                              onClick={() => handleApporteurStatClick(apporteur.admin_id, 'actif')}
                              className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-emerald-700 bg-emerald-100 dark:bg-emerald-950/30 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/40 transition-colors"
                            >
                              {apporteur.actifs}
                            </button>
                          </td>
                          <td className="py-2 text-center">
                            <button
                              onClick={() => handleApporteurStatClick(apporteur.admin_id, 'inactif')}
                              className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-yellow-700 bg-yellow-100 dark:bg-yellow-950/30 dark:text-yellow-300 hover:bg-yellow-200 dark:hover:bg-yellow-900/40 transition-colors"
                            >
                              {apporteur.inactifs}
                            </button>
                          </td>
                          <td className="py-2 text-center">
                            <button
                              onClick={() => handleApporteurStatClick(apporteur.admin_id, 'bloque')}
                              className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-red-700 bg-red-100 dark:bg-red-950/30 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/40 transition-colors"
                            >
                              {apporteur.bloques}
                            </button>
                          </td>
                          <td className="py-2 text-center">
                            <button
                              onClick={() => handleApporteurStatClick(apporteur.admin_id, 'ferme')}
                              className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-zinc-700 bg-zinc-100 dark:bg-zinc-900/40 dark:text-zinc-300 hover:bg-zinc-200 dark:hover:bg-zinc-900/60 transition-colors"
                            >
                              {apporteur.fermes ?? 0}
                            </button>
                          </td>
                          <td className="py-2 text-center">
                            <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-blue-700 bg-blue-100 dark:bg-blue-950/30 dark:text-blue-300">
                              {apporteur.total}
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                    {/* Ligne Total Général */}
                    <tr className="border-t-2 border-slate-300 dark:border-slate-600 bg-slate-50 dark:bg-slate-800/70 font-bold">
                      <td className="py-2 text-xs text-slate-900 dark:text-slate-100">Total</td>
                      <td className="py-2 text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-purple-700 bg-purple-200 dark:bg-purple-900/50 dark:text-purple-200">
                          {apporteurTotals.prospects}
                        </span>
                      </td>
                      <td className="py-2 text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-indigo-700 bg-indigo-200 dark:bg-indigo-900/50 dark:text-indigo-200">
                          {apporteurTotals.prospects_r1}
                        </span>
                      </td>
                      <td className="py-2 text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-violet-700 bg-violet-200 dark:bg-violet-900/50 dark:text-violet-200">
                          {apporteurTotals.prospects_r2_plus}
                        </span>
                      </td>
                      <td className="py-2 text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-emerald-700 bg-emerald-200 dark:bg-emerald-900/50 dark:text-emerald-200">
                          {apporteurTotals.actifs}
                        </span>
                      </td>
                      <td className="py-2 text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-yellow-700 bg-yellow-200 dark:bg-yellow-900/50 dark:text-yellow-200">
                          {apporteurTotals.inactifs}
                        </span>
                      </td>
                      <td className="py-2 text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-red-700 bg-red-200 dark:bg-red-900/50 dark:text-red-200">
                          {apporteurTotals.bloques}
                        </span>
                      </td>
                      <td className="py-2 text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-zinc-700 bg-zinc-200 dark:bg-zinc-800/70 dark:text-zinc-200">
                          {apporteurTotals.fermes}
                        </span>
                      </td>
                      <td className="py-2 text-center">
                        <span className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded-full text-[11px] font-bold text-blue-800 bg-blue-200 dark:bg-blue-900/50 dark:text-blue-200">
                          {apporteurTotals.total}
                        </span>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
            <div className="lg:col-span-2">
              <ActivityLogPanel />
            </div>
          </div>
        )}

        {/* Charts pour Admin et Apporteur */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="lg:col-span-2 bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-6 border-2 border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <h3 className="text-sm sm:text-lg font-bold text-slate-900 dark:text-slate-100 mb-3 sm:mb-4 flex items-center gap-2">
              <TrendingUp className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              Évolution du chiffre d'affaires
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={stats.revenue_by_month} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="month" stroke="#64748b" tick={{ fontSize: 11 }} />
                <YAxis
                  stroke="#64748b"
                  tick={{ fontSize: 10 }}
                  width={40}
                  tickFormatter={(value) => {
                    const num = Number(value);
                    if (num >= 1000) return `${(num / 1000).toFixed(0)}k`;
                    return Math.round(num).toString();
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 24px 24px -1px rgb(0 0 0 / 0.1)',
                    padding: '8px 12px',
                    fontSize: '12px'
                  }}
                  itemStyle={{ color: '#1e293b', fontWeight: 500 }}
                  labelStyle={{ color: '#64748b', marginBottom: '4px' }}
                  formatter={(value: any, name: any) => [
                    `${Math.round(Number(value)).toLocaleString('fr-FR')} €`,
                    name,
                  ]}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Line type="monotone" dataKey="revenue" stroke="#3b82f6" strokeWidth={2.5} name="CA (€ HT)" />
              </LineChart>
            </ResponsiveContainer>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-6 border-2 border-slate-200 dark:border-slate-700 shadow-sm overflow-hidden">
            <h3 className="text-sm sm:text-lg font-bold text-slate-900 dark:text-slate-100 mb-3 sm:mb-4">Commandes par statut</h3>
            <ResponsiveContainer width="100%" height={250}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="45%"
                  labelLine={false}
                  outerRadius={65}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.key] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'rgba(255, 255, 255, 0.95)',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 24px 24px -1px rgb(0 0 0 / 0.1)',
                    padding: '8px 12px',
                    fontSize: '12px'
                  }}
                  itemStyle={{ color: '#1e293b', fontWeight: 500 }}
                  labelStyle={{ color: '#64748b', marginBottom: '4px' }}
                />
                <Legend
                  layout="horizontal"
                  verticalAlign="bottom"
                  align="center"
                  wrapperStyle={{ fontSize: '10px', paddingTop: '8px' }}
                  iconSize={8}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Top Clients, Top Produits, et Commandes Récentes - Alignés en 3 blocs */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className="bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-6 border-2 border-slate-200 dark:border-slate-700 shadow-sm">
            <h3 className="text-sm sm:text-lg font-bold text-slate-900 dark:text-slate-100 mb-3 sm:mb-4">Top 5 Clients</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-2 sm:py-3 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">Client</th>
                    <th className="text-right py-2 sm:py-3 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">Com.</th>
                    <th className="text-right py-2 sm:py-3 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">CA HT</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.top_buyers.map((Client, index) => (
                    <tr key={index} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="py-2 sm:py-3 text-xs sm:text-sm text-slate-900 dark:text-slate-100 truncate max-w-[120px] sm:max-w-[100px]" title={Client.buyer_name}>{Client.buyer_name}</td>
                      <td className="py-2 sm:py-3 text-xs sm:text-sm text-right text-slate-600 dark:text-slate-400">{Client.order_count}</td>
                      <td className="py-2 sm:py-3 text-xs sm:text-sm text-right font-bold text-blue-600 dark:text-blue-400 whitespace-nowrap">
                        {Math.round(Client.total_spent).toLocaleString('fr-FR')} €
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-6 border-2 border-slate-200 dark:border-slate-700 shadow-sm">
            <h3 className="text-sm sm:text-lg font-bold text-slate-900 dark:text-slate-100 mb-3 sm:mb-4">Top 5 Produits</h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-2 sm:py-3 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">Produit</th>
                    <th className="text-right py-2 sm:py-3 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">Qté</th>
                    <th className="text-right py-2 sm:py-3 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">CA HT</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.top_products.map((product, index) => (
                    <tr key={index} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="py-2 sm:py-3 text-xs sm:text-sm text-slate-900 dark:text-slate-100 truncate max-w-[120px] sm:max-w-[100px]" title={product.product_name}>{product.product_name}</td>
                      <td className="py-2 sm:py-3 text-xs sm:text-sm text-right text-slate-600 dark:text-slate-400">{product.quantity_sold}</td>
                      <td className="py-2 sm:py-3 text-xs sm:text-sm text-right font-bold text-blue-600 dark:text-blue-400 whitespace-nowrap">
                        {Math.round(product.revenue).toLocaleString('fr-FR')} €
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white dark:bg-slate-900 rounded-xl p-4 sm:p-6 border-2 border-slate-200 dark:border-slate-700 shadow-sm">
            <h3 className="text-sm sm:text-lg font-bold text-slate-900 dark:text-slate-100 mb-3 sm:mb-4 flex items-center gap-2">
              <Calendar className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              Commandes récentes
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-2 sm:py-3 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">N°</th>
                    <th className="text-left py-2 sm:py-3 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">Date</th>
                    <th className="text-right py-2 sm:py-3 text-xs sm:text-sm font-medium text-slate-600 dark:text-slate-400">Montant</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.recent_orders.slice(0, 5).map((order: any) => (
                    <tr key={order.id} className="border-b border-slate-100 dark:border-slate-800 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                      <td className="py-2 sm:py-3 text-xs sm:text-sm font-medium text-slate-900 dark:text-slate-100 truncate max-w-[80px]" title={order.order_number}>{order.order_number}</td>
                      <td className="py-2 sm:py-3 text-xs sm:text-sm text-slate-600 dark:text-slate-400">
                        {new Date(order.order_date || order.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' })}
                      </td>
                      <td className="py-2 sm:py-3 text-xs sm:text-sm text-right font-bold text-slate-900 dark:text-slate-100 whitespace-nowrap">
                        {Math.round(order.total_amount).toLocaleString('fr-FR')} €
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>


      </div>
    </div>
  );
};

export default AchatDashboardTailwind;
